import React, { useState, useEffect, ChangeEvent, useRef, useMemo, useCallback, useContext } from 'react'
import { Accordion } from 'react-bootstrap'
import {
    Drawer,
    IconButton,
    Typography,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    Modal,
    TextField,
    Stack,
    Skeleton
} from '@mui/material'
import { ChevronRight, Refresh, Close, DataThresholding, OpenInNew } from '@mui/icons-material'

import '../../../styles/css/sidebar.css'
import { post, put, BlockOrGetData, customPost } from '../../../utils/http'
import { getXmlFile } from './processAnnotations'
import { LoadingBtn } from './uiElements'
import {
    Marker,
    Annotation,
    ImageData,
    PredictionResponse,
    TileRanks,
    GeoJSON
} from '../../../utils/types'
import sampleTile from '../../../assets/pixel-tile.jpg'
import LazyList from '../../common/LazyList'
import { modalStyle } from '../../../styles/js/common'
import { outputS3, tileAppUrl, isPrivate, tileApiKey } from '../../../utils/config'
import XmlList from './xmlList'
import JsonList from './JsonList'
import ChatComponent from './ChatComponent'
import { handleAnnoCheckbox } from './common'
import SysAnnotationsList from './SysAnnotationsList'
import UserAnnotationsList from './UserAnnotationsList'
import { ViewerContext } from './context'
import { defaultMarkerColor } from '../../../styles/js/colors.style'


interface Props {
    drawerOpen: boolean,
    closeDrawer: Function,
    imageData: ImageData,
    gsPredictionResult: PredictionResponse | {},
    sysGsAnnotations: Marker[], 
    modifiedGsAnnotations: Annotation[],
    bcrAnnotations: GeoJSON | null,
    showSysAnno: Function, 
    showUserAnno: Function, 
    showHeatMap: Function,
    updatePreditionResult: Function,
    showByUser: Function,
    showByCategory: Function,
    zoomToPolygon: Function,
    showJsonTileOverlay: Function,
    userAnnotationCancer: Function,
    processBCRHeatmap: Function,
    updateBCRScore: Function,
    point2Tile: Function,
    getGenomeData: Function,
    showGenomeHeatMap: Function,
    showGenomeHeatPoint: Function,
    renderJsonHeatmap: Function
    setXmlNames: Function
    setXmlClassFilter: Function
    showHeatMapByClass: Function
    sysAnnoLoader: boolean
    userAnnoLoader: boolean
}

interface BCRRequestOnProp {
    score: boolean
    heatmaps: boolean
    rankedTiles: boolean
}

interface HeatType {
    id: string
    name: string
    area: number
}

const ImageSideBar = ({
    drawerOpen,
    closeDrawer,
    imageData,
    gsPredictionResult,
    sysGsAnnotations, 
    modifiedGsAnnotations,
    bcrAnnotations,
    showSysAnno, 
    showUserAnno, 
    showHeatMap,
    updatePreditionResult,
    showByUser,
    showByCategory,
    zoomToPolygon,
    showJsonTileOverlay,
    userAnnotationCancer,
    processBCRHeatmap,
    updateBCRScore,
    point2Tile,
    getGenomeData,
    showGenomeHeatMap,
    showGenomeHeatPoint,
    renderJsonHeatmap,
    setXmlNames,
    setXmlClassFilter,
    showHeatMapByClass,
    sysAnnoLoader,
    userAnnoLoader
}: Props) => {
    const { BCRScore, imageZoomLvl, colorLib, bcrClassFilter, readonly, toggleLoading } = useContext(ViewerContext)

    const bcrRequestOn = useRef<BCRRequestOnProp>({score: false, heatmaps: false, rankedTiles: false})
    
    const [outputFiles, setOutputFiles] = useState<string[]>([])
    const [heatTypes, setHeatTypes] = useState<HeatType[] | null>(null)
    
    const [thresholdType, setThresholdType] = useState<string | null>(null)
    const [gp5Threshold, setGp5Threshold] = useState<string>(imageData.gp5_threshold)
    const [gp4Threshold, setGp4Threshold] = useState<string>(imageData.gp4_threshold)
    const [gp3Threshold, setGp3Threshold] = useState<string>(imageData.gp3_threshold)
    const [cancerThreshold, setCancerThreshold] = useState<string>(imageData.cancer_threshold)
    const [geneNameSearchKey, setGeneNameSearchKey] = useState<string>('')

    const [predResultLoading, setPredResultLoading] = useState<boolean>(false)
    const [treatmentOutcome, setTreatmentOutcome] = useState<boolean>(false)  // eslint-disable-line
    const [genotypePred, setGenotypePred] = useState<boolean>(false)  // eslint-disable-line
    const [thresholdApiLoading, setThresholdApiLoading] = useState<boolean>(false)

    const [tileRankData, setTileRankData] = useState<TileRanks | null>(null)
    const [tileRankList, setTileRankList] = useState<{top: any[], bottom: any[]}>({top: [], bottom: []})
    const [hasRankedTile, setHasRankedTile] = useState<boolean>(false)
    const [genomicPredList, setGenomicPredList] = useState<string[]>([])
    const [geneTypeList, setGeneTypeList] = useState<{id: string, list: {[x: string]: JSX.Element}[]}>({id: '', list: []})

    const pubOutputS3Bucket: string = outputS3 || ''
    const rankedTileLoc = imageData.output_s3 ? `https://${imageData.output_s3}.s3-us-west-2.amazonaws.com/${imageData.name}` : imageData.base_raw_download_url

    //------------------------------------------------------------------------

    const getPredictionFileList = useCallback(async () => {
        try {
            const files = await post(`S3/filecontents`, { directory : imageData.image_unique_id, s3_name: pubOutputS3Bucket })
            if (imageData.output_s3) {
                const s3Path = imageData.output_s3 ? imageData.output_s3.split('/').slice(1).join('/') : ''
                const slideName = imageData.name.split('/').pop()!
                const temp = await post(`S3/filecontents`, { directory : s3Path.length ? `${s3Path}/${slideName}` : imageData.name, s3_name: imageData.output_s3.split('/')[0] })
                files.push(...temp)
            }
            setOutputFiles(files)
        } catch (err) {
            setOutputFiles([])
        }
    }, [imageData, pubOutputS3Bucket])

    const loadImage = (payload: any) => {
        // Create tiles either using tiles from S3 or using tiling app
        return new Promise(async (resolve, reject) => {
            const img = new Image()
            img.crossOrigin = 'Anonymous'

            try {
                if (imageData.tiling_status.toLowerCase()!=='processed') {
                    const blob = await customPost(`${tileAppUrl}/tile/get-tile`, payload, {
                        responseType: 'blob',
                        headers: {
                            "Content-Type": "application/json",
                            'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
                            "X-API-Key": tileApiKey
                        }
                    })
                    if (!(blob instanceof Blob)) throw new Error("Response is not a Blob.")
                    const objectUrl = URL.createObjectURL(blob)
                    img.onload = () => {
                        URL.revokeObjectURL(objectUrl); // Clean up memory
                        resolve(img);
                    };
                    img.onerror = (err) => {
                        URL.revokeObjectURL(objectUrl);
                        reject(err);
                    };
                    img.src = objectUrl;
                } else {
                    img.onload = () => resolve(img)
                    img.onerror = reject
                    img.src = `${imageData.base_raw_download_url}/${payload.z}/${payload.y}/${payload.x}.png`
                }
            } catch (err) {
                reject(err)
            }
        })
    }

    const getTileSection = async (vertex: [number, number], payload: any) => {
        try {
            // Create canvas to combine tiles
            const canvas = document.createElement('canvas')
            canvas.width = 256
            canvas.height = 256
            const ctx = canvas.getContext('2d')
            payload.x = Math.floor(vertex[0] / 256)
            payload.y = Math.floor(vertex[1] / 256)
            const img: any = await loadImage(payload)
            ctx!.drawImage(img, 0, 0)
            return canvas.toDataURL()
        } catch (err) {
            return null
        }
    }

    const genTopBottomTilesList = () => {
        if (!tileRankData || !('top' in tileRankData) || !('bottom' in tileRankData)) return

        const createTileImage = (dataUrl: string | null, vertex: [number, number], color: string) => (
            <img src={dataUrl || sampleTile} alt={`tile`} width={80}
                style={{border: `1.5px solid ${color}`, cursor: 'pointer'}}
                onClick={() => point2Tile(vertex)}
            />
        )

        try {
            for (let i in tileRankData.top) {
                getTileSection(tileRankData.top[i], {z: imageZoomLvl})
                    .then(dataUrl => {
                        setTileRankList(prevState => ({
                            ...prevState,
                            top: [...prevState.top, createTileImage(dataUrl, tileRankData.top[i], 'orangered')]
                        }))
                    })
            }
            for (let i in tileRankData.bottom) {
                getTileSection(tileRankData.bottom[i], {z: imageZoomLvl})
                    .then(dataUrl => {
                        setTileRankList(prevState => ({
                            ...prevState,
                            bottom: [...prevState.bottom, createTileImage(dataUrl, tileRankData.bottom[i], 'dodgerblue')]
                        }))
                    })
            }
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        genTopBottomTilesList()
    }, [tileRankData])  // eslint-disable-line

    const getTopBottomTilesData = async () => {
        if (bcrRequestOn.current.rankedTiles || hasRankedTile || tileRankData) return

        bcrRequestOn.current.rankedTiles = true
        try {
            const payload = {
                s3_name: imageData.output_s3 || pubOutputS3Bucket,
                key: imageData.output_s3 ? `${imageData.name}/top/1.png` : `${imageData.image_unique_id}/top/1.png`
            }
            const fileAccess = await post('S3/getaccess', payload)
            const tileCheck = new Promise((resolve, reject) => {
                const img = new Image()
                img.crossOrigin = 'Anonymous'
                img.src = fileAccess.url
                img.onload = () => resolve(img)
                img.onerror = reject
            })
            await tileCheck
                .then(() => {
                    setHasRankedTile(true)
                    bcrRequestOn.current.rankedTiles = false
                })
                .catch(async (err) => {
                    try {
                        payload.key = imageData.output_s3 ? `${imageData.name}/display_tiles.json` : `${imageData.image_unique_id}/data/display_tiles.json`
                        const fileAccess = await post('S3/getaccess', payload)
                        const res = await BlockOrGetData(fileAccess.url)
                        setTileRankData(res.data)
                    } catch (err) {
                        console.warn('No display_tiles.json')
                        setTileRankData({top: {}, bottom: {}})
                    } finally {
                        bcrRequestOn.current.rankedTiles = false
                    }
                })
        } catch (err) {
            console.warn('No ranked tiles')
            bcrRequestOn.current.rankedTiles = false
        }
    }

    const showGenomicPrediction = async () => {
        try {
            const geneDir = '20x_224/model_outputs/virchow/topk/'
            const files = await post(`S3/node-list`, { directory : `${imageData.image_unique_id}/data/${geneDir}`, s3_name: pubOutputS3Bucket })
            if (imageData.output_s3) { 
                const s3Path = imageData.output_s3.split('/').slice(1).join('/')
                const slideDir = imageData.name.split('/').pop()!
                const res = await post(`S3/node-list`, { directory : s3Path.length ? `${s3Path}/${slideDir}/${geneDir}` : `${imageData.name}/${geneDir}`, s3_name: imageData.output_s3.split('/')[0] })
                files.push(...res)
            }
            if (files.length) setGenomicPredList(files)
            else throw Error('Empty')
        } catch(err) {
            setGenomicPredList([''])
        }
    }

    useEffect(() => {
        getPredictionFileList()
        if (isPrivate) {
            showGenomicPrediction()
        }
    }, [])  // eslint-disable-line

    //------------------------------------------------------------------------

    const updateCancerThreshold = (value: string) => setCancerThreshold(value)
    const updateGp3Threshold = (value: string) => setGp3Threshold(value)
    const updateGp4Threshold = (value: string) => setGp4Threshold(value)
    const updateGp5Threshold = (value: string) => setGp5Threshold(value)

    const saveCancerThreshold = async () => {
        try {
            if (!cancerThreshold.length) throw Error('no value')
            const res = await put(`Images/${imageData.id}`, {cancer_threshold: cancerThreshold})
            setCancerThreshold(res.cancer_threshold)
            imageData.cancer_threshold = res.cancer_threshold
        } catch (err) {
            setCancerThreshold(imageData.cancer_threshold)
        }
    }

    const saveGp3Threshold = async () => {
        try {
            if (!gp3Threshold.length) throw Error('no value')
            const res = await put(`Images/${imageData.id}`, {gp3_threshold: gp3Threshold})
            setGp3Threshold(res.gp3_threshold)
            imageData.gp3_threshold = res.gp3_threshold
        } catch (err) {
            setGp3Threshold(imageData.gp3_threshold)
        }
    }

    const saveGp4Threshold = async () => {
        try {
            if (!gp4Threshold.length) throw Error('no value')
            const res = await put(`Images/${imageData.id}`, {gp4_threshold: gp4Threshold})
            setGp4Threshold(res.gp4_threshold)
            imageData.gp4_threshold = res.gp4_threshold
        } catch (err) {
            setGp4Threshold(imageData.gp4_threshold)
        }
    }
    
    const saveGp5Threshold = async () => {
        try {
            if (!gp5Threshold.length) throw Error('no value')
            const res = await put(`Images/${imageData.id}`, {gp5_threshold: gp5Threshold})
            setGp5Threshold(res.gp5_threshold)
            imageData.gp5_threshold = res.gp5_threshold
        } catch (err) {
            setGp5Threshold(imageData.gp5_threshold)
        }
    }

    const reloadPredResults = async () => {
        setPredResultLoading(true)
        try {
            await updatePreditionResult()
        } catch (err) {
            console.log(err)
        } finally {
            setPredResultLoading(false)
        }
    }
    
    const saveThreshold = async () => {
        setThresholdApiLoading(true)
        if (thresholdType === 'cancer') await saveCancerThreshold()
        else if (thresholdType === 'gp3') await saveGp3Threshold()
        else if (thresholdType === 'gp4') await saveGp4Threshold()
        else if (thresholdType === 'gp5') await saveGp5Threshold()
        await updatePreditionResult()
        setThresholdApiLoading(false)
        setThresholdType(null)
    }

    const handleThresholdChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (thresholdType === 'cancer') updateCancerThreshold(e.target.value)
        else if (thresholdType === 'gp3') updateGp3Threshold(e.target.value)
        else if (thresholdType === 'gp4') updateGp4Threshold(e.target.value)
        else updateGp5Threshold(e.target.value)
    }

    const closeThresholdModal = () => {
        setThresholdType(null)
        // Revert the threshold edits
        if (thresholdType === 'cancer') {
            if (cancerThreshold !== imageData.cancer_threshold) setCancerThreshold(imageData.cancer_threshold)
        } else if (thresholdType === 'gp3') {
            if (gp3Threshold !== imageData.gp3_threshold) setGp3Threshold(imageData.gp3_threshold)
        } else if (thresholdType === 'gp4') {
            if (gp4Threshold !== imageData.gp4_threshold) setGp4Threshold(imageData.gp4_threshold)
        } else if (thresholdType === 'gp5') {
            if (gp5Threshold !== imageData.gp5_threshold) setGp5Threshold(imageData.gp5_threshold)
        }
    }

    const openThresholdModal = (value: string) => setThresholdType(value)

    const PredResultList = useMemo(() => {
        return ('tissue_area' in gsPredictionResult && gsPredictionResult.tissue_area > 0
            ? <div className='d-flex flex-column align-items-start gap-3 p-2' style={{width: "max-content"}}>
                <Typography>Total tissue area : {gsPredictionResult.tissue_area} mm²</Typography>
                <Typography>Total cancer area : {gsPredictionResult.cancer_area} mm²</Typography>
                <div className="d-flex align-items-center gap-2">
                    <Typography>Cancer Volume (System) : {gsPredictionResult.tumor_volume}%</Typography>
                    {!readonly
                        ? <button className='btn btn-sm btn-light py-0 px-1 text-secondary' onClick={() => openThresholdModal('cancer')} title='Threshold'>
                            <DataThresholding /> {cancerThreshold}%
                        </button>
                        : null
                    }
                </div>
                <div onClick={(e) => userAnnotationCancer(e)} className="d-flex align-items-center gap-2">
                    <Typography>Cancer Volume (User) : {gsPredictionResult.tumor_volume_user}%</Typography>
                    {!readonly
                        ? <input
                            style={{cursor: "pointer"}} 
                            type="checkbox"
                            title='Toggle cancer class'
                            className="form-check-input m-0 toggle_slide_anno shadow-none"
                            readOnly
                        />
                        : null
                    }
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Typography>GP3 : {gsPredictionResult.gp3}%</Typography>
                    {!readonly
                        ? <button className='btn btn-sm btn-light py-0 px-1 text-secondary' onClick={() => openThresholdModal('gp3')} title='Threshold'>
                            <DataThresholding /> {gp3Threshold}%
                        </button>
                        : null
                    }
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Typography>GP4 : {gsPredictionResult.gp4}%</Typography>
                    {!readonly
                        ? <button className='btn btn-sm btn-light py-0 px-1 text-secondary' onClick={() => openThresholdModal('gp4')} title='Threshold'>
                            <DataThresholding /> {gp4Threshold}%
                        </button>
                        : null
                    }
                </div>
                <Typography>GP4-CRIB : {gsPredictionResult.gp4_crib}%</Typography>
                <div className="d-flex align-items-center gap-2">
                    <Typography>GP5 : {gsPredictionResult.gp5}%</Typography>
                    {!readonly
                        ? <button className='btn btn-sm btn-light py-0 px-1 text-secondary' onClick={() => openThresholdModal('gp5')} title='Threshold'>
                            <DataThresholding /> {gp5Threshold}%
                        </button>
                        : null
                    }
                </div>
                <Typography>PNI : {gsPredictionResult.pni}</Typography>
                <Typography>HGPIN : {gsPredictionResult.hgpin}</Typography>
                <Typography>Diagnosis/Gleason Score : {gsPredictionResult.gleason_grade}</Typography>
                <a className="btn btn-sm btn-outline-primary" role="button" href={`../report/${imageData.id}`} target='_blank' rel="noreferrer">
                    Show Report
                </a>
            </div>
            : ["Start", "Processing"].includes(imageData.tissuearea_status)
                ? <Typography className='text-secondary'>Prediction has been started, please wait for it to complete</Typography>
                : ["Stop", "Pending"].includes(imageData.tissuearea_status)
                    ? <Typography className='text-secondary'>Tissue area prediction not done</Typography>
                    : imageData.tissuearea_status === 'Processed'
                        ? <Typography className='text-secondary'>Loading please wait</Typography>
                        : <Typography className='text-secondary'>Tissue area data not found</Typography>
        )
    }, [gsPredictionResult, gp5Threshold, gp3Threshold, gp4Threshold, cancerThreshold, imageData])  // eslint-disable-line

    //------------------------------------------------------------------------

    const getBCRScore = async () => {
        bcrRequestOn.current.score = true
        try {
            const BCRFileCheckPayload = {
                s3_name: imageData.output_s3 || pubOutputS3Bucket,
                directory: imageData.output_s3 ? imageData.name : imageData.image_unique_id,
                file_type: imageData.file_type || 'svs'
            }
            const res = await post('S3/bcr-score-file', BCRFileCheckPayload)
            if ('key' in res && res.key.length) {
                const fileAccess = await post('S3/getaccess', { s3_name: BCRFileCheckPayload.s3_name, key: res.key })
                const scoreData = await BlockOrGetData(fileAccess.url)
                const score = Number(scoreData.data.bcr_score)
                put(`Images/${imageData.id}`, { bcr_score: score });
                updateBCRScore(Number(score.toFixed(2)))
            } else throw Error('File not found')
        } catch (err) {
            console.log(err)
            updateBCRScore(NaN)
        } finally {
            bcrRequestOn.current.score = false
        }
    }

    const getBCRHeatmap = async () => {
        bcrRequestOn.current.heatmaps = true
        toggleLoading('bcr-heatmap', true)
        try {
            const BCRFileCheckPayload = {
                s3_name: imageData.output_s3 || pubOutputS3Bucket,
                directory: imageData.output_s3 ? imageData.name : imageData.image_unique_id,
                file_type: imageData.file_type || 'svs'
            }
            const res = await post('S3/bcr-heatmap-file', BCRFileCheckPayload)
            if ('key' in res && res.key.length) {
                const { xml, size } = await getXmlFile(res.key, BCRFileCheckPayload.s3_name, null)  // eslint-disable-line
                const temp = await processBCRHeatmap(xml)
                setHeatTypes(temp)
            } else throw Error('File not found')
        } catch (err) {
            console.log(err)
            setHeatTypes([])
        } finally {
            bcrRequestOn.current.heatmaps = false
            toggleLoading('bcr-heatmap', false)
        }
    }

    const getBCRData = () => {
        if (BCRScore===null && !bcrRequestOn.current.score) getBCRScore()
        if (!bcrAnnotations && !bcrRequestOn.current.heatmaps) getBCRHeatmap()
        if (isPrivate) getTopBottomTilesData()
    }

    //------------------------------------------------------------------------

    const showTreatOutcomePrediction = () => setTreatmentOutcome(prevState => !prevState)

    //------------------------------------------------------------------------

    const showGenoTypePrediction = () => setGenotypePred(prevState => !prevState)

    //------------------------------------------------------------------------

    const showGenome = async (key: string, filename: string, outputS3: string | null, listId: string) => {
        setGeneNameSearchKey('')
        if (geneTypeList.id !== key) setGeneTypeList({id: '', list: []})
        try {
            const s3Path = outputS3 ? outputS3.split('/').slice(1).join('/') : ''
            const slideDir = filename.split('/').pop()!
            const gridKey = s3Path.length ? `${s3Path}/${slideDir}/20x_224/grid.csv` : `${filename}/20x_224/grid.csv`
            const gene_names = await getGenomeData(key, gridKey, listId)
            const temp = [...gene_names].sort().map((i: string) => {
                return {
                    [i]: <ul key={i} className="fs-6 fw-normal d-flex align-items-center justify-content-between gap-3">
                        <Typography className='d-flex align-items-center'>
                            <input
                                type='checkbox'
                                id={`${i}-gene-heatmap`}
                                onChange={e => showGenomeHeatMap(e, listId, i)}
                                className='form-check-input mt-0 toggle_slide_anno'
                            />&nbsp;&nbsp;{i}
                        </Typography>
                        <Typography className='d-flex align-items-center text-secondary'>
                            <input
                                type='checkbox'
                                id={`${i}-gene-heatpoint`}
                                onChange={e => showGenomeHeatPoint(e, listId, i)}
                                className='form-check-input mt-0 toggle_slide_anno'
                            />&nbsp;&nbsp;Points
                        </Typography>
                    </ul>
                }
            })
            setGeneTypeList({id: key, list: temp})
        } catch (err) {
            console.log(err)
            setGeneTypeList({id: '', list: [{'0': <ul key='0' className="fs-6 fw-normal">N/A</ul>}]})
        }
    }

    const handleGeneNameSearch = (e: any) => setGeneNameSearchKey(e.target.value)

    const handleCloseGeneNameSearch = () => setGeneNameSearchKey('')

    //------------------------------------------------------------------------

    const showTissueOutline = (e: any) => {
        try {
            handleAnnoCheckbox(e.target.id)
            const temp = [`${imageData.image_unique_id}/data/tissue_glands.xml`,
                `${imageData.image_unique_id}.${imageData.file_type}/data/tissue_glands.xml`,
                `${imageData.image_unique_id}/data/${imageData.image_unique_id}_tissue_glands.xml`,
                `${imageData.image_unique_id}.${imageData.file_type}/data/${imageData.image_unique_id}_tissue_glands.xml`,
                `${imageData.image_unique_id}/${imageData.image_unique_id}_tissue_glands.xml`,
                `${imageData.image_unique_id}.${imageData.file_type}/${imageData.image_unique_id}_tissue_glands.xml`
            ]
            const tissueFile = outputFiles?.find(i => typeof i === 'string' && temp.some(x => typeof x === 'string' && i.endsWith(x)))

            if (e.target.checked) {
                if (tissueFile) renderXMLData(tissueFile)
                else throw Error("Tissue area not predicted")
            } else {
                if (tissueFile) removeXMLData(tissueFile)
                else throw Error("Tissue area not predicted")
            }
        } catch (err) {
            console.log(err)
        }
    }

    //------------------------------------------------------------------------

    const removeXMLData = useCallback((fileName: string) => {
        // Reset class filter without re-render for this xml when closing
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            prevState[fileName] = []
            return prevState
        })
        setXmlNames((prevState: string[]) => {
            const list = prevState.filter(i => i && i !== fileName)
            if (!list.length) list.push('')
            return list
        })
    }, [])  // eslint-disable-line

    const renderXMLData = useCallback((fileName: string) => {
        // Reset class filter without re-render for this xml when closing
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            prevState[fileName] = []
            return prevState
        })
        setXmlNames((prevState: string[]) => {
            if (prevState.includes(fileName)) return prevState
            return [...prevState, fileName]
        })
    }, [])  // eslint-disable-line

    const removeXMLClassData = useCallback((fileName: string, className: string) => {
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            if (fileName in prevState) {
                const temp = prevState[fileName].filter(i => i && i!==className)
                return {...prevState, [fileName]: temp}
            } else return prevState
        })
    }, [])  // eslint-disable-line

    const addXMLClassData = useCallback((fileName: string, className: string) => {
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            if (!(fileName in prevState)) prevState[fileName] = []
            const temp = prevState[fileName]
            if (!prevState[fileName].includes(className)) temp.push(className)
            return {...prevState, [fileName]: temp}
        })
    }, [])  // eslint-disable-line

    const MemoizedChat = useMemo(() =>
        <ChatComponent imageId={imageData.id} />
    , [imageData])

    const MemoizedXmlList = useMemo(() =>
        <XmlList
            imageData={imageData}
            list={outputFiles}
            refreshList={getPredictionFileList}
            renderXMLData={renderXMLData}
            removeXMLData={removeXMLData}
            addXMLClassData={addXMLClassData}
            removeXMLClassData={removeXMLClassData}
        />
    , [imageData, outputFiles])  // eslint-disable-line

    const MemoizedJsonList = useMemo(() =>
        <JsonList
            imageData={imageData}
            list={outputFiles}
            refreshList={getPredictionFileList}
            showJsonTileOverlay={showJsonTileOverlay}
            renderJsonHeatmap={renderJsonHeatmap}
        />
    , [imageData, outputFiles])  // eslint-disable-line

    const MemoizedSysAnnotationsList = useMemo(() =>
        <SysAnnotationsList
            sysMarkers={sysGsAnnotations}
            userMarkers={modifiedGsAnnotations}
            showByCategory={showByCategory}
            zoomToMarker={zoomToPolygon}
            sysAnnoLoader={sysAnnoLoader}
        />
    , [sysGsAnnotations, modifiedGsAnnotations, sysAnnoLoader])  // eslint-disable-line

    const MemoizedUserAnnotationsList = useMemo(() =>
        <UserAnnotationsList
            userMarkers={modifiedGsAnnotations}
            showByUser={showByUser}
            zoomToMarker={zoomToPolygon}
            userAnnoLoader={userAnnoLoader}
        />
    , [modifiedGsAnnotations, userAnnoLoader])  // eslint-disable-line

    const MemoizedRankedTiles = useMemo(() => {
        if (!isPrivate) return
        return (
            tileRankData
                ? Object.keys(tileRankData.top).length
                    ? <div className='d-flex flex-column gap-2'>
                        <p className='fw-normal m-0'>Top tiles</p>
                        <div className='d-flex gap-2'>
                            {hasRankedTile
                                ? 'top' in tileRankData && Object.entries(tileRankData.top).map(i => (
                                    <object
                                        key={i[0]}
                                        onClick={() => point2Tile(i[1][0])}
                                        data={`${rankedTileLoc}/top/${i[0]}.png`}
                                        type="image/png"
                                        width={80}
                                        style={{ border: '1.5px solid orangered' }}
                                    >
                                        <img src={sampleTile} alt="tile" width={80} />
                                    </object>
                                ))
                                : tileRankList.top.length ? tileRankList.top : 'N/A'
                            }
                        </div>
                        <p className='fw-normal m-0'>Bottom tiles</p>
                        <div className='d-flex gap-2'>
                            {hasRankedTile
                                ? 'bottom' in tileRankData && Object.entries(tileRankData.bottom).map((i: any) => (
                                    <object
                                        key={i[0]}
                                        onClick={() => point2Tile(i[1][0])}
                                        data={`${rankedTileLoc}/bottom/${i[0]}.png`}
                                        type="image/png"
                                        width={80}
                                        style={{ border: '1.5px solid dodgerblue' }}
                                    >
                                        <img src={sampleTile} alt="tile" width={80} />
                                    </object>
                                ))
                                : tileRankList.bottom.length ? tileRankList.bottom : 'N/A'
                            }
                        </div>
                    </div>
                    : <div className='d-flex justify-content-between align-items-center gap-3'>
                        <Typography className='text-secondary'>No top and bottom tiles</Typography>
                        {/* <IconButton size='small' onClick={getTopBottomTilesData}><Refresh /></IconButton> */}
                    </div>
                : <Skeleton variant="rounded" height={34} />
        )
    }, [tileRankData, tileRankList, hasRankedTile])  // eslint-disable-line

    const MemoizedBCRHeatTypes = useMemo(() => {
        return (
            heatTypes
                ? heatTypes.length
                    ? <div className='d-flex flex-column gap-2 mb-3'>
                        {heatTypes.map((i, index) => {
                            const color = colorLib.find(x => x.name.toLowerCase()===i.name.toLowerCase())
                            return (
                                <div
                                    key={index}
                                    style={{width: 'fit-content'}}
                                    className='d-flex align-items-center gap-2 cursor-pointer px-2'
                                    onClick={() => showHeatMapByClass(i.name)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={bcrClassFilter.includes(i.name)}
                                        className="form-check-input m-0 shadow-none"
                                        style={{
                                            pointerEvents: 'none',
                                            backgroundColor: color ? color.color : defaultMarkerColor,
                                            borderColor: color ? color.color : defaultMarkerColor,
                                            outline: 'none'
                                        }}
                                        readOnly
                                    />
                                    <Typography>{i.name}</Typography>
                                </div>
                            )
                        })}
                    </div>
                    : <Typography className='text-secondary'>No heatmap</Typography>
                : <Skeleton variant="rounded" height={24} />
        )
    }, [heatTypes, colorLib, bcrClassFilter])  // eslint-disable-line

    return (
        <Drawer open={drawerOpen} anchor="right" variant="persistent" PaperProps={{sx: {position: 'absolute', zIndex: 999}, elevation: 3}}>
            <Box className="d-flex align-items-center p-1 justify-content-start">
                <IconButton onClick={() => closeDrawer()}>
                    <ChevronRight />
                </IconButton>
            </Box>

            <div className='p-1'>
                <Accordion as="h6">
                    <Accordion.Item key='1' eventKey="1">
                        <span className="d-flex align-items-center justify-content-center">
                            <input
                                id="check-all-anno"
                                type="checkbox"
                                className="form-check-input m-0 ms-3 me-2 toggle_slide_anno"
                                style={{cursor: "pointer"}}
                                onChange={(e) => showSysAnno(e)}
                            />
                            <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                <Typography>System Annotations</Typography>
                            </Accordion.Header>
                        </span>
                        <Accordion.Body>
                            {MemoizedSysAnnotationsList}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion as="h6">
                    <Accordion.Item key='2' eventKey="2">
                        <span className="d-flex align-items-center justify-content-center">
                            <input
                                id="check-user-anno"
                                type="checkbox"
                                className="form-check-input m-0 ms-3 me-2 toggle_slide_anno"
                                style={{cursor: "pointer"}}
                                onChange={(e) => showUserAnno(e)}
                            />
                            <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                <Typography>User Annotations</Typography>
                            </Accordion.Header>
                        </span>
                        <Accordion.Body>
                            {MemoizedUserAnnotationsList}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion as="h6">
                    <Accordion.Item key='3' eventKey="3">
                        <Accordion.Header className="no-shadow btn-gap">
                            <Typography>Diagnostic Predictions</Typography>
                        </Accordion.Header>
                        <Accordion.Body style={{ position: 'relative' }}>
                            <Box sx={{position: 'absolute', right: '1rem', top: '1rem', zIndex: 2}}>
                                <IconButton onClick={reloadPredResults} disabled={predResultLoading} title="Reload">
                                    <Refresh />
                                </IconButton>
                            </Box>
                            {predResultLoading
                                ? <Stack spacing={2} className='p-2'>
                                    {Array.from({length: 10}, (_, i) => i).map(i => (<Skeleton key={i} variant="rounded" height={24} />))}
                                </Stack>
                                : PredResultList
                            }
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion as="h6">
                    <Accordion.Item key='4' eventKey='4'>
                        <span onClick={getBCRData} className="d-flex align-items-center justify-content-center">
                            <input
                                id="check-user-bcr"
                                type="checkbox"
                                className="form-check-input m-0 ms-3 me-2 toggle_slide_anno"
                                style={{cursor: "pointer"}}
                                onChange={(e) => showHeatMap(e)}
                            />
                            <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                <Typography>Prognostic Outcome Predictions</Typography>
                            </Accordion.Header>
                        </span>
                        <Accordion.Body className='position-relative'>
                            <IconButton className='bcr-score-reload' onClick={getBCRScore} size='small'><Refresh /></IconButton>
                            {BCRScore!==null
                                ? BCRScore
                                    ? <>
                                        <Typography>PathomIQ Score: {BCRScore}</Typography>
                                        {BCRScore > 0.55
                                            ? <Typography sx={{width: 'fit-content'}} className='text-danger'>Patient is at high risk of metastasis</Typography>
                                            : BCRScore < 0.45
                                                ? <Typography sx={{width: 'fit-content'}} className='text-primary'>Patient is at low risk of biochemical recurrence and metastasis</Typography>
                                                : <Typography sx={{width: 'fit-content'}} className='text-primary'>Patient is at high risk of biochemical recurrence</Typography>
                                        }
                                    </>
                                    : <Typography className='text-secondary'>No score</Typography>
                                : <Skeleton variant="rounded" height={48} />
                            }
                            <br/>
                            {MemoizedRankedTiles}
                            <br/>
                            {MemoizedBCRHeatTypes}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {isPrivate
                    ? <Accordion as="h6">
                        <Accordion.Item key='5' eventKey='5'>
                            <Accordion.Header onClick={showTreatOutcomePrediction} className="no-shadow btn-gap">
                                <Typography>Treatment Outcome Predictions</Typography>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Typography className='text-secondary'>Coming soon</Typography>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    : null
                }

                {isPrivate
                    ? <Accordion as="h6">
                        <Accordion.Item key='6' eventKey='6'>
                            <Accordion.Header onClick={showGenoTypePrediction} className="no-shadow btn-gap">
                                <Typography>Cancer Genotype Predictions</Typography>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Typography className='text-secondary'>Coming soon</Typography>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    : null
                }

                {isPrivate
                    ? <Accordion as="h6">
                        <Accordion.Item key='7' eventKey='7'>
                            <Accordion.Header className="no-shadow btn-gap">
                                <Typography>Genomic Predictions</Typography>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Accordion>
                                    {genomicPredList.length
                                        ? genomicPredList.map((i: any) => {
                                            if (!i.length) return <Typography key={i} className='text-secondary'>N/A</Typography>
                                            const name = i.split('/').filter((x: string) => x.length).slice(-1)[0]
                                            return (
                                                <Accordion.Item key={i} eventKey={i}>
                                                    <Accordion.Header className="no-shadow" onClick={() => showGenome(i, imageData.name, imageData.output_s3, name)}>
                                                        <Typography className='text-capitalize'>{name}</Typography>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        {geneTypeList.list.length
                                                            ? <>
                                                                <FormControl className='w-100 mb-3' variant="outlined">
                                                                    <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                                                                    <OutlinedInput
                                                                        id="outlined-adornment-search"
                                                                        type='text'
                                                                        onChange={handleGeneNameSearch}
                                                                        value={geneNameSearchKey}
                                                                        endAdornment={
                                                                            <InputAdornment position="end">
                                                                                <IconButton
                                                                                    aria-label='search gene name'
                                                                                    onClick={handleCloseGeneNameSearch}
                                                                                    edge="end"
                                                                                    disabled={!geneNameSearchKey.length}
                                                                                >
                                                                                    <Close />
                                                                                </IconButton>
                                                                            </InputAdornment>
                                                                        }
                                                                        label="Search"
                                                                    />
                                                                </FormControl>
                                                                <LazyList items={geneTypeList.list} searchKey={geneNameSearchKey} /> 
                                                            </>
                                                            : <CircularProgress size={24} />
                                                        }
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            )
                                        })
                                        : <LoadingBtn />
                                    }
                                </Accordion>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    : null
                }

                <Accordion as="h6">
                    <Accordion.Item key='8' eventKey='8'>
                        <Accordion.Header className="no-shadow btn-gap">
                            <Typography>Comments</Typography>
                        </Accordion.Header>
                        <Accordion.Body style={{maxWidth: 500}}>
                            {MemoizedChat}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion as="h6">
                    <Accordion.Item key='9' eventKey='9'>
                        <Accordion.Header className="no-shadow btn-gap">
                            <Typography>XML Files</Typography>
                        </Accordion.Header>
                        <Accordion.Body>
                            {MemoizedXmlList}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {isPrivate
                    ? <Accordion as="h6">
                        <Accordion.Item key='10' eventKey="10">
                            <Accordion.Header className="no-shadow btn-gap">
                                <Typography>JSON Files</Typography>
                            </Accordion.Header>
                            <Accordion.Body>
                                {MemoizedJsonList}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    : null
                }

                <Accordion>
                    <Box className="p-3">
                        <span className="d-flex align-items-center">
                            <input
                                id="tissue-outline"
                                type="checkbox"
                                className="form-check-input m-0 me-2 toggle_slide_anno"
                                style={{ cursor: "pointer" }}
                                onChange={showTissueOutline}
                            />
                            <Typography>Show tissue outline</Typography>
                        </span>
                    </Box>
                </Accordion>

                {isPrivate && <Accordion>
                    <Box className="p-3">
                        <Button
                            size="small"
                            href={`/viewer-v2/${imageData.id}`}
                            variant="contained"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <OpenInNew color="inherit" fontSize='small' />&nbsp;&nbsp;
                            New viewer preview
                        </Button>
                    </Box>
                </Accordion>}
            </div>
            
            {/* Threshold modal */}
            <Modal open={thresholdType !== null} onClose={closeThresholdModal}>
                <Box sx={modalStyle}>
                    <Box className='d-flex flex-column gap-4'>
                        <Typography variant="h5" component="h1">Modify {thresholdType} threshold</Typography>
                        <TextField
                            label="Threshold value"
                            variant="outlined"
                            defaultValue={thresholdType === 'cancer'
                                ? cancerThreshold
                                : thresholdType === 'gp3'
                                    ? gp3Threshold
                                    : thresholdType === 'gp4'
                                        ? gp4Threshold
                                        : gp5Threshold
                            }
                            onChange={handleThresholdChange}
                            autoComplete="off"
                        />
                        <Box className='d-flex gap-3 justify-content-end'>
                            {thresholdApiLoading
                                ? <CircularProgress size={32} />
                                : <>
                                    <Button variant="outlined" onClick={closeThresholdModal}>Close</Button>
                                    <Button variant="contained" onClick={saveThreshold}
                                        disabled={thresholdType === 'cancer'
                                            ? cancerThreshold === imageData.cancer_threshold ? true : false
                                            : thresholdType === 'gp3'
                                                ? gp3Threshold === imageData.gp3_threshold ? true : false
                                                : thresholdType==='gp4'
                                                    ? gp4Threshold === imageData.gp4_threshold ? true : false
                                                    : gp5Threshold === imageData.gp5_threshold ? true : false
                                        }
                                    >
                                        Save
                                    </Button>
                                </>
                            }
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Drawer>
    )
}

export default ImageSideBar